import {
  Checkbox,
  Dropdown,
  Field,
  Input,
  Option,
} from '@fluentui/react-components';
import { useFetch } from '@moko/ui';
import {
  getPricingItems,
  type IPricingItem,
} from '../../services/pricingService';
import type { IMetadata } from '../../services/metadataService';
import { actualFields } from '@moko/ui/src/Work/actuals/types';
import type {
  ActualsConfig,
  ActualField,
} from '@moko/ui/src/Work/actuals/types';

export function BillingActualsOptions({
  metadata,
  onChange,
}: {
  metadata?: IMetadata;
  onChange: (value: IMetadata) => void;
}) {
  const pricingItems = useFetch<IPricingItem[]>({
    queryKey: ['pricingItems'],
    queryFn: async (controller) =>
      (await getPricingItems(controller, 500, 1)).items,
  });
  const eligibleItems = (pricingItems.data ?? [])
    .filter(
      (item) =>
        item.isDeleted !== true &&
        item.calculationMethod === 'UnitRate' &&
        item.chargeRole === 'AdditionalCharge'
    )
    .sort((a, b) => a.sortOrder - b.sortOrder);
  if (!metadata) return null;
  const config: ActualsConfig = metadata.billingActuals ?? { fields: [] };
  const update = (patch: Partial<ActualsConfig>) =>
    onChange({ ...metadata, billingActuals: { ...config, ...patch } });
  return (
    <section style={{ display: 'grid', gap: 12, maxWidth: 650 }}>
      <h3>Inspection actuals</h3>
      <p>
        Select the facts collected on this Work form. Published changes apply to
        Works pinned to the new form version. Existing Works retain their pinned
        configuration.
      </p>
      {(Object.keys(actualFields) as ActualField[]).map((key) => (
        <Checkbox
          key={key}
          label={actualFields[key]}
          checked={config.fields.includes(key)}
          onChange={(_, value) =>
            update({
              fields: value.checked
                ? [...config.fields, key]
                : config.fields.filter((k) => k !== key),
              quantitySources: Object.fromEntries(
                Object.entries(config.quantitySources ?? {}).filter(
                  ([, field]) => value.checked || field !== key
                )
              ),
            })
          }
        />
      ))}
      <h4>Final billing quantities</h4>
      <p>
        Select a unit-rate additional charge for each billable quantity. New
        Orders capture its rate and included units or minimum hours. Items not
        included in the estimate are charged only from approved actuals.
        Configure rates and parameters before placing an Order.
      </p>
      {config.fields
        .filter((key) =>
          [
            'anchorCount',
            'waitHours',
            'equipmentHours',
            'propertyHours',
            'travelHours',
          ].includes(key)
        )
        .map((key) => {
          const mappings = config.quantitySources ?? {};
          const code = Object.entries(mappings).find(
            ([, field]) => field === key
          )?.[0];
          const selected = eligibleItems.find(
            (item) => item.code.toUpperCase() === code?.toUpperCase()
          );
          const unavailable = !!code && !!pricingItems.data && !selected;
          return (
            <Field
              key={key}
              label={`${actualFields[key]} pricing item`}
              validationMessage={
                pricingItems.error
                  ? 'Pricing items could not be loaded. Reload the page to try again.'
                  : unavailable
                  ? `Pricing item "${code}" is unavailable or no longer eligible. Select another item or clear the mapping.`
                  : undefined
              }
              hint={
                !pricingItems.loading &&
                !pricingItems.error &&
                eligibleItems.length === 0
                  ? 'No unit-rate additional charge items are available. Configure them in Global settings > Pricing items.'
                  : undefined
              }
            >
              <Dropdown
                clearable
                disabled={pricingItems.loading || !!pricingItems.error}
                placeholder={
                  pricingItems.loading
                    ? 'Loading pricing items…'
                    : 'Select pricing item'
                }
                value={
                  selected ? `${selected.name} (${selected.code})` : code ?? ''
                }
                selectedOptions={
                  selected ? [selected.code] : code ? [code] : []
                }
                onOptionSelect={(_, value) => {
                  const next = Object.fromEntries(
                    Object.entries(mappings).filter(
                      ([, field]) => field !== key
                    )
                  );
                  if (value.optionValue) next[value.optionValue] = key;
                  update({ quantitySources: next });
                }}
              >
                {eligibleItems.map((item) => (
                  <Option
                    key={item.code}
                    value={item.code}
                    disabled={Object.entries(mappings).some(
                      ([mappedCode, field]) =>
                        mappedCode.toUpperCase() === item.code.toUpperCase() &&
                        field !== key
                    )}
                  >
                    {`${item.name} (${item.code})`}
                  </Option>
                ))}
                {unavailable && (
                  <Option
                    value={code}
                    disabled
                  >{`${code} (unavailable)`}</Option>
                )}
              </Dropdown>
            </Field>
          );
        })}
      <p>
        Purchase authorization uses existing fields on the originating Order.
        Missing authorization prevents purchase approval. The cap is a total
        budget across purchase receipts.
      </p>
      <Field label="Order purchase cap field name">
        <Input
          value={config.purchaseCapField ?? ''}
          onChange={(_, v) => update({ purchaseCapField: v.value || null })}
        />
      </Field>
      <Field label="Order purchase currency field name">
        <Input
          value={config.purchaseCurrencyField ?? ''}
          onChange={(_, v) =>
            update({ purchaseCurrencyField: v.value || null })
          }
        />
      </Field>
      <p>
        These settings collect reported facts only. They do not enable
        additional tracking or calculate fees.
      </p>
    </section>
  );
}
